"use client";

import * as React from "react";
import { Checkbox } from "@/app/components/ui/checkbox";
import { BackendButton } from "@/app/components/backend/backend-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  GripVertical,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { createCellRenderer } from "./cell-renderers";
import { getFallbackValue } from "@/app/utils/helpers/backend/backend-table/fallback-utils";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  BackendTablePropsT,
  BackendTableRowT,
  SortDirectionT,
  BackendTableColumnT,
} from "@/app/types/backend-table";

function getColumnWidthClass(column: BackendTableColumnT): string | undefined {
  return column.width;
}

/**
 * Backend Table Components and Hooks
 *
 * This module provides a comprehensive table system for backend data management.
 * It includes sorting functionality, row selection, pagination, and reusable components
 * for consistent table experiences across the application.
 *
 * Features:
 * - Sortable columns with visual indicators
 * - Row selection with select all functionality
 * - Pagination with configurable items per page
 * - Responsive design with hover states
 * - TypeScript support with generic types
 * - Customizable column rendering
 *
 * @example
 * ```tsx
 * // Basic usage with hook
 * const { selectedRows, handleSort, handleRowSelect } = useBackendTable(data);
 *
 * // With table component
 * <BackendTable
 *   columns={columns}
 *   data={data}
 *   selectedRows={selectedRows}
 *   onRowSelect={handleRowSelect}
 *   onSort={handleSort}
 * />
 * ```
 */

/**
 * BackendTable Component
 *
 * A comprehensive table component for backend data management with advanced features
 * including sorting, row selection, pagination, and customizable column rendering.
 *
 * Features:
 * - Sortable columns with visual indicators (chevron icons)
 * - Row selection with checkbox controls
 * - Select all functionality with indeterminate state
 * - Pagination with configurable items per page
 * - Responsive design with hover states and alternating row colors
 * - Special rendering for common field types (links, dates, IDs, logos, external links)
 * - External link support with custom link text and icons
 * - Customizable styling and column widths
 *
 * @param columns - Array of column configurations defining table structure
 * @param data - Array of data rows to display in the table
 * @param selectedRows - Array of currently selected row IDs
 * @param onRowSelect - Callback function for individual row selection
 * @param onSelectAll - Callback function for select all functionality
 * @param onSort - Callback function for column sorting
 * @param sortColumn - Currently sorted column key
 * @param sortDirection - Current sort direction (asc/desc/null)
 * @param currentPage - Current page number (1-based)
 * @param totalItems - Total number of items across all pages
 * @param itemsPerPage - Number of items to display per page
 * @param onItemsPerPageChange - Callback function for changing items per page
 * @param onPageChange - Callback function for page navigation (optional, enables navigation buttons)
 * @param className - Additional CSS classes for styling
 * @param routeConfig - Route configuration for detail page links (optional)
 *
 * @example
 * ```tsx
 * const columns = [
 *   { key: "title", label: "Title", sortable: true },
 *   { key: "status", label: "Status", sortable: true },
 *   { key: "createdAt", label: "Created", sortable: true }
 * ];
 *
 * // For articles with detail page links
 * <BackendTable
 *   columns={columns}
 *   data={articles}
 *   selectedRows={selectedRows}
 *   onRowSelect={handleRowSelect}
 *   onSelectAll={handleSelectAll}
 *   onSort={handleSort}
 *   sortColumn={sortColumn}
 *   sortDirection={sortDirection}
 *   currentPage={currentPage}
 *   totalItems={totalItems}
 *   itemsPerPage={itemsPerPage}
 *   onItemsPerPageChange={handleItemsPerPageChange}
 *   routeConfig={{
 *     basePath: "beitraege",
 *     slugField: "slug"
 *   }}
 * />
 *
 * // For employees with different route
 * <BackendTable
 *   columns={columns}
 *   data={employees}
 *   selectedRows={selectedRows}
 *   onRowSelect={handleRowSelect}
 *   onSelectAll={handleSelectAll}
 *   onSort={handleSort}
 *   sortColumn={sortColumn}
 *   sortDirection={sortDirection}
 *   currentPage={currentPage}
 *   totalItems={totalItems}
 *   itemsPerPage={itemsPerPage}
 *   onItemsPerPageChange={handleItemsPerPageChange}
 *   routeConfig={{
 *     basePath: "mitarbeiter",
 *     slugField: "id"
 *   }}
 * />
 * ```
 */
export function BackendTable({
  columns,
  data,
  selectedRows,
  onRowSelect,
  onSelectAll,
  onSort,
  sortColumn,
  sortDirection,
  currentPage,
  totalItems,
  itemsPerPage,
  onItemsPerPageChange,
  onPageChange,
  className,
  routeConfig,
  enableDragDrop = false,
  emptyMessage = "Keine Daten gefunden.",
}: BackendTablePropsT) {
  const previousPageRef = React.useRef(currentPage);

  // Scroll to top of main content area only after an actual page change.
  React.useEffect(() => {
    if (previousPageRef.current !== currentPage) {
      previousPageRef.current = currentPage;
      const mainContent = document.getElementById("backend-main-content");
      if (mainContent) {
        mainContent.scrollTo({ top: 0, behavior: "instant" });
      }
    }
  }, [currentPage]);

  // Calculate pagination slice
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data.slice(startIndex, endIndex);

  // Select all logic based on all data (not just paginated)
  // This ensures "Select All" works on all filtered data, not just current page
  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected =
    selectedRows.length > 0 && selectedRows.length < data.length;

  const getSortIcon = (columnKey: string) => {
    if (sortColumn !== columnKey) {
      return <ChevronsUpDown size={16} className="text-gray-400" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp size={16} className="text-gray-600" />
    ) : (
      <ChevronDown size={16} className="text-gray-600" />
    );
  };

  const startItem = totalItems > 0 ? startIndex + 1 : 0;
  const endItem = Math.min(endIndex, totalItems);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const renderCell = (column: BackendTableColumnT, row: BackendTableRowT) => {
    const value = row[column.key];

    // Use custom renderer if provided
    if (column.render) {
      return column.render(value, row, routeConfig);
    }

    // Use type-based renderer
    if (column.type) {
      const renderer = createCellRenderer(column.type, routeConfig);
      return renderer(value, row, routeConfig);
    }

    // Default rendering with fallback
    return (
      <span className="text-sm text-gray-600">{getFallbackValue(value)}</span>
    );
  };

  // Sortable row component for drag & drop
  const SortableRow = ({ row }: { row: BackendTableRowT }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: row.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={cn(
          "h-16 border-t border-[var(--c-border-soft)] transition-colors",
          selectedRows.includes(row.id)
            ? "bg-[var(--c-accent-bg)]"
            : "hover:bg-gray-50",
          isDragging && "opacity-50"
        )}
      >
        <td className="px-4 py-3 align-middle">
          <div className="flex items-center">
            <Checkbox
              checked={selectedRows.includes(row.id)}
              onCheckedChange={(checked) =>
                onRowSelect(row.id, checked as boolean)
              }
              className="bg-background dark:bg-background size-5 cursor-pointer data-[state=checked]:!border-[var(--c-accent)] data-[state=checked]:!bg-[var(--c-accent)]"
            />
          </div>
        </td>
        {enableDragDrop && (
          <td className="px-2 py-3 align-middle">
            <div className="flex items-center">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab text-gray-400 hover:cursor-grabbing hover:text-gray-600"
              >
                <GripVertical size={16} />
              </div>
            </div>
          </td>
        )}
        {columns.map((column) => (
          <td
            key={column.key}
            className={cn(
              "px-4 py-3 align-middle",
              getColumnWidthClass(column)
            )}
          >
            <div className="flex max-w-full min-w-0 items-center">
              {renderCell(column, row)}
            </div>
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Table */}
      <div className="relative w-full scrollbar-thin overflow-x-auto rounded-[16px] border border-[var(--c-border-card)] bg-white">
        <table className="w-full border-collapse whitespace-nowrap">
          <thead>
            <tr className="bg-[var(--c-surface)]">
              <th className="w-12 px-4 py-3 align-middle">
                <div className="flex items-center">
                  <Checkbox
                    checked={allSelected}
                    ref={(el) => {
                      if (el && "indeterminate" in el) {
                        (el as HTMLInputElement).indeterminate = someSelected;
                      }
                    }}
                    onCheckedChange={onSelectAll}
                    className="dark:bg-background bg-background size-5 cursor-pointer data-[state=checked]:!border-[var(--c-accent)] data-[state=checked]:!bg-[var(--c-accent)]"
                  />
                </div>
              </th>
              {enableDragDrop && (
                <th className="w-8 px-2 py-3 align-middle">
                  <div className="flex items-center">
                    <span className="sr-only">Sortieren</span>
                  </div>
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    "px-4 py-3 text-left align-middle font-[family-name:var(--font-backend-mono)] text-[10.5px] font-medium tracking-[0.07em] text-[var(--c-label)] uppercase",
                    column.sortable &&
                      "cursor-pointer hover:bg-[var(--c-surface-sunken)]",
                    getColumnWidthClass(column)
                  )}
                  onClick={() => column.sortable && onSort(column.key)}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.label}</span>
                    {column.sortable && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr className="h-16">
                <td
                  colSpan={
                    enableDragDrop
                      ? columns.length + 2 // checkbox + drag handle + columns
                      : columns.length + 1 // checkbox + columns
                  }
                  className="px-4 py-4 text-center align-middle"
                >
                  <p className="text-sm text-gray-500">{emptyMessage}</p>
                </td>
              </tr>
            ) : enableDragDrop ? (
              <SortableContext
                items={paginatedData.map((row) => row.id)}
                strategy={verticalListSortingStrategy}
              >
                {paginatedData.map((row) => (
                  <SortableRow key={row.id} row={row} />
                ))}
              </SortableContext>
            ) : (
              paginatedData.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    "h-16 border-t border-[var(--c-border-soft)] transition-colors",
                    selectedRows.includes(row.id)
                      ? "bg-[var(--c-accent-bg)]"
                      : "hover:bg-gray-50"
                  )}
                >
                  <td className="px-4 py-3 align-middle">
                    <div className="flex items-center">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onCheckedChange={(checked) =>
                          onRowSelect(row.id, checked as boolean)
                        }
                        className="bg-background dark:bg-background size-5 cursor-pointer data-[state=checked]:!border-[var(--c-accent)] data-[state=checked]:!bg-[var(--c-accent)]"
                      />
                    </div>
                  </td>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        "px-4 py-3 align-middle",
                        getColumnWidthClass(column)
                      )}
                    >
                      <div className="flex max-w-full min-w-0 items-center">
                        {renderCell(column, row)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {onPageChange && (
              <>
                <BackendButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={!canGoPrevious}
                  aria-label="Vorherige Seite"
                >
                  <ChevronLeft size={16} />
                  <span className="hidden md:inline">Zurück</span>
                </BackendButton>
                <span className="hidden min-w-24 text-center text-sm text-gray-600 md:inline-block">
                  Seite {currentPage} von {totalPages}
                </span>
                <BackendButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={!canGoNext}
                  aria-label="Nächste Seite"
                >
                  <span className="hidden md:inline">Weiter</span>
                  <ChevronRight size={16} />
                </BackendButton>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden text-sm text-gray-600 md:block">
              {startItem}-{endItem} von {totalItems}
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden text-sm text-gray-600 md:inline">
                Pro Seite:
              </span>
              <Select
                value={
                  itemsPerPage >= totalItems ? "all" : itemsPerPage.toString()
                }
                onValueChange={(value) =>
                  onItemsPerPageChange(
                    value === "all" ? totalItems : Number(value)
                  )
                }
              >
                <SelectTrigger size="sm" className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="all">Alle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * useBackendTable Hook
 *
 * A React hook that provides comprehensive table state management including row selection,
 * sorting, pagination, and items per page configuration.
 *
 * Features:
 * - Row selection state management with select all functionality
 * - Sorting state with column and direction tracking
 * - Pagination state with current page tracking
 * - Items per page configuration with automatic page reset
 * - Memoized handlers for optimal performance
 *
 * @param initialData - Initial data array for select all functionality (optional)
 * @returns Object containing table state and handler functions
 *
 * @example
 * ```tsx
 * const {
 *   selectedRows,
 *   sortColumn,
 *   sortDirection,
 *   currentPage,
 *   itemsPerPage,
 *   handleRowSelect,
 *   handleSelectAll,
 *   handleSort,
 *   handlePageChange,
 *   handleItemsPerPageChange
 * } = useBackendTable(data);
 *
 * // Use with BackendTable component
 * <BackendTable
 *   columns={columns}
 *   data={data}
 *   selectedRows={selectedRows}
 *   onRowSelect={handleRowSelect}
 *   onSelectAll={handleSelectAll}
 *   onSort={handleSort}
 *   sortColumn={sortColumn}
 *   sortDirection={sortDirection}
 *   currentPage={currentPage}
 *   itemsPerPage={itemsPerPage}
 *   onItemsPerPageChange={handleItemsPerPageChange}
 * />
 * ```
 */
export function useBackendTable(initialData: BackendTableRowT[] = []) {
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] =
    React.useState<SortDirectionT>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(50);
  const pendingSelectionScrollTopRef = React.useRef<number | null>(null);

  React.useLayoutEffect(() => {
    const scrollTop = pendingSelectionScrollTopRef.current;
    if (scrollTop === null) {
      return;
    }

    pendingSelectionScrollTopRef.current = null;
    document.getElementById("backend-main-content")?.scrollTo({
      top: scrollTop,
      behavior: "instant",
    });
  }, [selectedRows]);

  const preserveSelectionScroll = () => {
    const mainContent = document.getElementById("backend-main-content");
    pendingSelectionScrollTopRef.current = mainContent?.scrollTop ?? null;
  };

  const handleRowSelect = (rowId: string, selected: boolean) => {
    preserveSelectionScroll();
    setSelectedRows((prev) =>
      selected ? [...prev, rowId] : prev.filter((id) => id !== rowId)
    );
  };

  const handleSelectAll = (selected: boolean) => {
    preserveSelectionScroll();
    setSelectedRows(selected ? initialData.map((row) => row.id) : []);
  };

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
  };

  // Memoized so the scroll-to-top effect (which depends on onPageChange) only
  // runs on an actual page change — not on every re-render (e.g. row selection).
  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = React.useCallback(
    (newItemsPerPage: number) => {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1); // Reset to first page
    },
    []
  );

  return {
    selectedRows,
    sortColumn,
    sortDirection,
    currentPage,
    itemsPerPage,
    handleRowSelect,
    handleSelectAll,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
  };
}

/**
 * sortDataByColumn Helper Function
 *
 * A utility function that sorts an array of data items based on a specified column
 * and sort direction. This function handles different data types appropriately
 * and provides consistent sorting behavior.
 *
 * Features:
 * - Case-insensitive string comparison using localeCompare
 * - Numeric comparison for number values
 * - Null-safe handling of undefined/null values
 * - Immutable sorting (returns new array)
 * - Support for ascending and descending sort directions
 *
 * @param data - Array of data items to sort
 * @param sortColumn - Column key to sort by (null for no sorting)
 * @param sortDirection - Sort direction: "asc", "desc", or null
 * @returns New sorted array (original array is not modified)
 *
 * @example
 * ```tsx
 * const users = [
 *   { id: 1, name: "John", age: 30 },
 *   { id: 2, name: "Alice", age: 25 },
 *   { id: 3, name: "Bob", age: 35 }
 * ];
 *
 * // Sort by name ascending
 * const sortedByName = sortDataByColumn(users, "name", "asc");
 *
 * // Sort by age descending
 * const sortedByAge = sortDataByColumn(users, "age", "desc");
 *
 * // No sorting
 * const unsorted = sortDataByColumn(users, null, null);
 * ```
 */
export function sortDataByColumn<T extends Record<string, unknown>>(
  data: T[],
  sortColumn: string | null,
  sortDirection: "asc" | "desc" | null
): T[] {
  if (!sortColumn || !sortDirection) {
    return data;
  }

  return [...data].sort((a, b) => {
    const aValue = a[sortColumn as keyof T];
    const bValue = b[sortColumn as keyof T];

    if (aValue === bValue) return 0;

    let comparison = 0;
    if (typeof aValue === "string" && typeof bValue === "string") {
      comparison = aValue.localeCompare(bValue);
    } else if (aValue < bValue) {
      comparison = -1;
    } else {
      comparison = 1;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });
}

/**
 * useBackendSort Hook
 *
 * A React hook that provides sorting functionality for data arrays. This hook manages
 * sort state and automatically sorts data based on the current sort parameters.
 *
 * Features:
 * - Automatic data sorting based on sort column and direction
 * - Toggle sort direction when clicking the same column
 * - Reset sort functionality
 * - Memoized sorted results for performance
 * - Initial sort configuration support
 *
 * @param data - Array of data items to sort
 * @param initialSortColumn - Initial column to sort by (optional)
 * @param initialSortDirection - Initial sort direction (optional)
 * @returns Object containing sorted data, sort state, and sort handlers
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { sortedData, sortColumn, sortDirection, handleSort } = useBackendSort(data);
 *
 * // With initial sort
 * const { sortedData, handleSort } = useBackendSort(data, "name", "asc");
 *
 * // With reset functionality
 * const { sortedData, handleSort, resetSort } = useBackendSort(data);
 *
 * // Use with BackendTable component
 * <BackendTable
 *   columns={columns}
 *   data={sortedData}
 *   onSort={handleSort}
 *   sortColumn={sortColumn}
 *   sortDirection={sortDirection}
 * />
 * ```
 */
export function useBackendSort<T extends Record<string, unknown>>(
  data: T[],
  initialSortColumn: string | null = null,
  initialSortDirection: "asc" | "desc" | null = null
) {
  const [sortColumn, setSortColumn] = React.useState<string | null>(
    initialSortColumn
  );
  const [sortDirection, setSortDirection] = React.useState<
    "asc" | "desc" | null
  >(initialSortDirection);

  // Sort data based on current sort parameters
  const sortedData = React.useMemo(() => {
    return sortDataByColumn(data, sortColumn, sortDirection);
  }, [data, sortColumn, sortDirection]);

  const handleSort = React.useCallback(
    (columnKey: string) => {
      if (sortColumn === columnKey) {
        if (sortDirection === "asc") {
          setSortDirection("desc");
        } else if (sortDirection === "desc") {
          setSortColumn(null);
          setSortDirection(null);
        } else {
          setSortDirection("asc");
        }
      } else {
        // Set new column with ascending direction
        setSortColumn(columnKey);
        setSortDirection("asc");
      }
    },
    [sortColumn, sortDirection]
  );

  const resetSort = React.useCallback(() => {
    setSortColumn(null);
    setSortDirection(null);
  }, []);

  return {
    sortedData,
    sortColumn,
    sortDirection,
    handleSort,
    resetSort,
  };
}
